import {
  type BiddingStrategy,
  type CompiledTemplateCampaign,
  type Placement,
  type TemplateCampaignOverride,
  type TemplatePortfolioLayer,
  templateTargetKey,
} from "@adpilot/shared";

export const PREVIEW_GROUPS = ["core", "long_tail", "auto", "category", "harvest"] as const;
export type PreviewGroup = (typeof PREVIEW_GROUPS)[number];

const GROUP_OF_LAYER: Record<TemplatePortfolioLayer, PreviewGroup> = {
  core: "core",
  long_tail: "long_tail",
  auto_close_match: "auto",
  auto_substitutes: "auto",
  category_target: "category",
  category_theme: "category",
  competitor_brand: "harvest",
  misspelling: "harvest",
  non_english: "harvest",
};

export const groupOfLayer = (layer: TemplatePortfolioLayer): PreviewGroup => GROUP_OF_LAYER[layer];

export const BIDDING_STRATEGIES: BiddingStrategy[] = [
  "AUTO_FOR_SALES",
  "LEGACY_FOR_SALES",
  "MANUAL",
];

/** Drawer order: TOS / ROS / PP / AB. */
export const PLACEMENTS: Placement[] = [
  "PLACEMENT_TOP",
  "PLACEMENT_REST_OF_SEARCH",
  "PLACEMENT_PRODUCT_PAGE",
  "SITE_AMAZON_BUSINESS",
];

export function isAutomatic(campaign: CompiledTemplateCampaign): boolean {
  return campaign.payload.adGroup.targetingType === "automatic";
}

/** Table 默认出价: the ad group default, or the enabled auto group's bid. */
export function campaignDefaultBid(campaign: CompiledTemplateCampaign): number {
  const { adGroup } = campaign.payload;
  if (adGroup.targetingType === "manual") return adGroup.manualTargeting.defaultBid;
  const auto = adGroup.autoTargetingBids;
  if (auto.mode === "default") return auto.defaultBid;
  return Object.values(auto.targets).find((target) => target.enabled)?.bid ?? 0;
}

/** One editable target row in the drawer; `key` is the override targetBids key. */
export interface PreviewTarget {
  key: string;
  kind: "keyword" | "auto" | "category" | "theme";
  label: string;
  /** Match type for keywords, auto group for auto targets, category id for category. */
  detail: string;
  enabled: boolean;
  /** null only for a disabled auto group without its own bid. */
  bid: number | null;
  removable: boolean;
  priceGreaterThan?: number;
  priceLessThan?: number;
}

export function campaignTargets(campaign: CompiledTemplateCampaign): PreviewTarget[] {
  const { adGroup } = campaign.payload;
  if (adGroup.targetingType === "automatic") {
    const auto = adGroup.autoTargetingBids;
    if (auto.mode === "default") return [];
    return Object.entries(auto.targets).map(([group, target]) => ({
      key: group,
      kind: "auto",
      label: group,
      detail: group,
      enabled: target.enabled,
      bid: target.bid,
      removable: false,
    }));
  }
  const manual = adGroup.manualTargeting;
  switch (manual.type) {
    case "keyword":
      return manual.targets.map((target) => ({
        key: templateTargetKey(target.text, target.matchType),
        kind: "keyword",
        label: target.text,
        detail: target.matchType,
        enabled: true,
        bid: target.bid,
        removable: true,
      }));
    case "category":
      return manual.targets.map((target) => ({
        key: "category",
        kind: "category",
        label: target.categoryId,
        detail: target.categoryId,
        enabled: true,
        bid: target.bid,
        removable: false,
        priceGreaterThan: target.priceGreaterThan,
        priceLessThan: target.priceLessThan,
      }));
    case "theme":
      return manual.targets.map((target) => ({
        key: "theme",
        kind: "theme",
        label: target.theme,
        detail: target.theme,
        enabled: true,
        bid: target.bid,
        removable: false,
      }));
    default:
      return [];
  }
}

export interface DrawerEdits {
  name: string;
  dailyBudget: number;
  biddingStrategy: BiddingStrategy;
  /** undefined for automatic campaigns, whose ad group has no default bid. */
  defaultBid: number | undefined;
  /** Missing placement = no adjustment. */
  placements: Partial<Record<Placement, number>>;
  targetBids: Record<string, number>;
  removedTargets: string[];
  addedNegatives: string[];
  removedNegatives: string[];
}

const lower = (text: string) => text.toLowerCase();

/**
 * Folds the drawer's edits into the stored override. The drawer starts from the
 * compiled (already overridden) campaign, so only fields that differ from it are
 * written; earlier overrides stay.
 */
export function mergeOverride(
  campaign: CompiledTemplateCampaign,
  existing: TemplateCampaignOverride | undefined,
  edits: DrawerEdits,
): TemplateCampaignOverride | null {
  const { campaign: settings } = campaign.payload;
  const next: TemplateCampaignOverride = { ...existing };
  let changed = false;
  const set = <K extends keyof TemplateCampaignOverride>(
    key: K,
    value: TemplateCampaignOverride[K],
  ) => {
    next[key] = value;
    changed = true;
  };

  const name = edits.name.trim();
  if (name !== campaign.name) set("name", name);
  if (edits.dailyBudget !== settings.dailyBudget) set("dailyBudget", edits.dailyBudget);
  if (edits.biddingStrategy !== settings.biddingStrategy) {
    set("biddingStrategy", edits.biddingStrategy);
  }
  if (edits.defaultBid !== undefined && edits.defaultBid !== campaignDefaultBid(campaign)) {
    set("defaultBid", edits.defaultBid);
  }

  const placements = PLACEMENTS.flatMap((placement) => {
    const percentage = edits.placements[placement];
    return percentage === undefined ? [] : [{ placement, percentage }];
  });
  const current = new Map(settings.placementAdjustments.map((p) => [p.placement, p.percentage]));
  if (
    placements.length !== current.size ||
    placements.some(({ placement, percentage }) => current.get(placement) !== percentage)
  ) {
    set("placementAdjustments", placements);
  }

  const bids = new Map(campaignTargets(campaign).map((target) => [target.key, target.bid]));
  const changedBids = Object.entries(edits.targetBids).filter(
    ([key, bid]) => bids.get(key) !== bid,
  );
  if (changedBids.length > 0) {
    set("targetBids", { ...existing?.targetBids, ...Object.fromEntries(changedBids) });
  }
  if (edits.removedTargets.length > 0) {
    set("removedTargets", [
      ...new Set([...(existing?.removedTargets ?? []), ...edits.removedTargets]),
    ]);
  }

  // A negative added earlier and removed now just leaves addedNegatives (and vice versa).
  let added = existing?.addedNegatives ?? [];
  let removed = existing?.removedNegatives ?? [];
  for (const text of edits.removedNegatives) {
    if (added.some((item) => lower(item) === lower(text))) {
      added = added.filter((item) => lower(item) !== lower(text));
    } else removed = [...removed, text];
  }
  for (const text of edits.addedNegatives) {
    if (removed.some((item) => lower(item) === lower(text))) {
      removed = removed.filter((item) => lower(item) !== lower(text));
    } else added = [...added, text];
  }
  if (edits.addedNegatives.length > 0 || edits.removedNegatives.length > 0) {
    set("addedNegatives", added);
    set("removedNegatives", removed);
  }

  return changed ? next : null;
}
